import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { getApiBaseUri, getQaOrigin, isQaHost } from 'library/constants/api';
import { OnboardingShell, OutlineButton, IMPORT_SECTIONS, ob } from 'components/Onboarding';
import { clearImportEdit, startImportEdit } from 'library/helpers/importEditFlow';
import { isAdminUser } from 'library/utils/isAdmin';
import styled from 'styled-components';
import { FiArrowLeft, FiArrowRight, FiCheckCircle, FiEdit2, FiEye, FiInfo, FiMapPin } from 'react-icons/fi';
import { LuBath, LuBedDouble, LuSparkles, LuUsers, LuVideo } from 'react-icons/lu';
import { GhostButton, PrimaryButton } from 'components/Onboarding';
import {
  TitleRow,
  PreviewPill,
  ReviewCard,
  Mosaic,
  EditPill,
  Body,
  Main,
  HeadRow,
  Stats,
  BlockRow,
  Lines,
  Offers,
  LiveNote,
  Aside,
  BottomRow,
} from './ReviewListing.styles';
import {
  PreviewBanner,
  PillButton,
  SectionCard,
  SectionHead,
  Row,
  DescriptionHtml,
  TagGrid,
  Tag,
  SubHeading,
  PhotoCategory,
  Muted,
  EmptyNote,
  StatRow,
  ConfirmSection,
  FloatingPreview,
  SuccessWrap,
  PropertyCardBox,
  TextLinkButton,
} from './Confirmation.styles';
import PropertyService from 'service/PropertyService';
import HostPlanPurchaseService from 'service/HostPlanPurchaseService';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import CalendarService, { HostCalendarDayDTO } from 'service/CalendarService';
import { useDispatch, useSelector } from 'react-redux';
import { useOnboardingBackConfirm } from 'library/hooks/useOnboardingBackConfirm';
import { CUSTOM_POLICY_CODE, describePolicy } from 'library/helpers/customCancellationPolicy';
import {
  overwritePropertyDetails,
  addStoredImages,
  overwritePricing,
  addBookingSettings,
  addThumbnailUrl,
  updateStore,
  setImages,
  addVideoUrl,
  addWalkthroughVideoUrl,
} from 'features/placeSlice';
import {
  transformPropertyDataFromServer,
  transformPropertyPricing,
} from './utils';
import { getCatalog as getCancellationCatalog } from 'service/CancellationPolicyService';
import type { CancellationPolicyTemplate } from 'features/placeSlice';
import LocationService from 'service/LocationService';
import PricingService from 'service/PricingService';
import { isEmpty } from 'lodash';
import { HOME_PAGE, IMAGE_CATEGORY_OPTIONS } from 'settings/constant';
import { notification } from 'antd';
import UniqueStayService from 'service/UniqueStayService';
import { isAdminSession } from 'library/helpers/isAdminSession';
import { clearDraft, readDraft } from 'library/helpers/uniqueStayDraft';
import {
  getApiResponseMessage,
  getUserFriendlyError,
} from 'library/helpers/errorMessage';
import DOMPurify from 'dompurify';
import { transformBookingSettingsFromApi } from 'library/helpers/bookingSettingsFromApi';

interface PropertyDetails {
  offeringsMap?: Record<string, any>;
  amenitiesObject?: any[];
}

interface ReduxState {
  place: { name: string; other?: string };
  type: { name: string };
  subType: { name: string };
  location: {
    country: string;
    addressLine1?: string;
    addressLine2: string;
    pincode: string;
    landmark?: string;
    district?: string;
    city: string;
    state: string;
  };
  detail: {
    title: string;
    description: string;
    /** Brand-free copy guests see (from the server); title / description stay the host's own. */
    displayTitle?: string;
    displayDescription?: string;
    bnbUrl: string;
    property3dUrl: string;
    uspsAndView: string;
    elderlyFriendly: '' | 'yes' | 'no';
    childFriendly: '' | 'yes' | 'no';
    petFriendly: '' | 'yes' | 'no';
    securityGuard24x7: '' | 'yes' | 'no';
    propertyStructureType: '' | 'standalone' | 'gated_complex';
    poolLength: string;
    poolBreadth: string;
    poolDepth: string;
    parkingDescription: string;
    flexibleCheckinType: '' | 'self' | 'assisted' | 'front_desk' | 'digital';
  };
  pricingDetails: {
    maxGuestAllowed: { numberOfGuests: number };
    defaultPrice: number;
    additionalCharges: Array<{
      additionalChargeId: number;
      chargeType: string;
      pricePerNight: number;
      additionalChargeName: string;
    }>;
    discounts: {
      lessThan24Hours: number;
      between24HoursTo72Hours: number;
      equalToOrMoreThan72Hours: number;
      equalToOrMoreThan7Days: number;
    };
    basePrice: {
      lessThan24Hours: { start: number; end: number };
      between24HoursTo72Hours: { start: number; end: number };
      equalToOrMoreThan72Hours: { start: number; end: number };
      equalToOrMoreThan7Days: { start: number; end: number };
    };
  };
  bookingSettings: {
    checkInTime: string;
    checkOutTime: string;
    minDayBooking: number | string;
    maxDayBooking: number | string;
    cancellationPolicyCode: string | null;
    restrictions?: string[];
    partialPaymentAllowed: boolean;
    partialPaymentPercentage: number;
  };
  propertyId: string;
  additionalChargesMap: Record<number, string>;
  onboardingStage: number;
  amenitiesObject: any[];
  offeringsMap: Record<string, string>;
}

interface PropertyPhotos {
  imageUrlList?: Record<string, string[]>;
  thumbnail?: Record<string, string>;
}

interface ApiResponse {
  responseInformation: {
    responseCode: number;
  };
  result: any;
}

interface PropertyTypeListMap {
  [key: number]: any;
}

interface PropertyOfferingsMap {
  [key: number]: string;
}

const getPreviewBaseUrl = (): string => {
  if (typeof window === 'undefined') return 'https://unpaqd.com';
  const h = window.location.hostname;
  return h.includes('localhost') || h.includes('127.0.0.1') || isQaHost(h)
    ? getQaOrigin(h)
    : 'https://unpaqd.com';
};

const openPreviewInNewTab = async (propertyId: string | number, title?: string | null) => {
  const base = getPreviewBaseUrl();
  const slug = (title || 'property')
    .toString()
    .trim()
    .replace(/[:\\/&()%+#@!?=]/g, ' ')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '') || 'property';
  const fallbackUrl = `${base}/property/${propertyId}/${slug}`;
  try {
    const token = localStorage.getItem('jwtToken');
    const res = await axios.get(
      `${getApiBaseUri()}/property/preview-token?propertyId=${propertyId}`,
      token ? { headers: { Authorization: `Bearer ${token}` } } : {},
    );
    const previewToken = res?.data?.previewToken;
    const backUrl = encodeURIComponent(window.location.href);
    const url = previewToken
      ? `${fallbackUrl}?preview_token=${encodeURIComponent(previewToken)}&back_url=${backUrl}`
      : fallbackUrl;
    window.open(url, '_blank');
  } catch {
    window.open(fallbackUrl, '_blank');
  }
};

const buildPreviewUrl = (propertyId: string | number, title?: string | null): string => {
  const slug = (title || 'property')
    .toString()
    .trim()
    .replace(/[:\\/&()%+#@!?=]/g, ' ')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '') || 'property';
  return `${getPreviewBaseUrl()}/property/${propertyId}/${slug}`;
};

const normalizeImageUrlForCompare = (url: string): string => {
  if (!url) return '';
  try {
    const parsed = new URL(url);
    return `${parsed.origin}${parsed.pathname}`;
  } catch (_) {
    return url.split('?')[0].split('#')[0];
  }
};

/** Single-line summary for confirmation (aligned with Booking Settings partial payment). */
const formatBookingPaymentModeSummary = (settings: {
  partialPaymentAllowed?: boolean;
  partialPaymentPercentage?: number;
}): string => {
  const reserveEnabled = settings.partialPaymentAllowed !== false;
  if (!reserveEnabled) {
    return 'Full payment';
  }
  const raw = Number(settings.partialPaymentPercentage);
  const pct =
    Number.isFinite(raw) && raw >= 1 && raw <= 100 ? Math.round(raw) : 50;
  return `Full payment + Reserve at ${pct}%`;
};

interface SuccessViewProps {
  detail?: { title?: string; displayTitle?: string };
  location?: { city?: string; state?: string };
  propertyPhotos?: { thumbnail?: Record<string, string> };
  onEdit: () => void;
  onDashboard: () => void;
  onAddAnother: () => void;
}

/** "Submitted for review": shown after the host confirms and submits their listing. */
const SuccessView: React.FC<SuccessViewProps> = ({
  detail,
  location,
  propertyPhotos,
  onEdit,
  onDashboard,
  onAddAnother,
}) => {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const thumbnailUrl = React.useMemo(() => {
    if (!propertyPhotos?.thumbnail) return null;
    const values = Object.values(propertyPhotos.thumbnail);
    return values.length > 0 ? values[0] : null;
  }, [propertyPhotos?.thumbnail]);

  const locationLabel = [location?.city, location?.state, 'India'].filter(Boolean).join(', ');

  return (
    <DoneWrap>
      <span className="badge">
        <FiCheckCircle size={52} />
      </span>
      <h1>Submitted for review</h1>
      <p>
        Our team checks every new listing before it goes live - usually within two working days.
        We’ll update you via email once it’s approved.
      </p>

      <DoneCard>
        {thumbnailUrl ? (
          <img src={thumbnailUrl} alt={detail?.displayTitle || detail?.title || 'Property'} />
        ) : (
          <span className="ph" />
        )}
        <div>
          <span className="pill">Pending review</span>
          <b>{detail?.displayTitle || detail?.title || 'Your property'}</b>
          {locationLabel ? <span className="loc">{locationLabel}</span> : null}
        </div>
      </DoneCard>

      <DoneActions>
        <PrimaryButton type="button" onClick={onEdit}>
          Edit my listing <FiEdit2 size={16} />
        </PrimaryButton>
        <OutlineButton type="button" onClick={onDashboard}>
          Go to dashboard <FiArrowRight size={16} />
        </OutlineButton>
        <OutlineButton type="button" onClick={onAddAnother}>
          Add another property
        </OutlineButton>
      </DoneActions>
    </DoneWrap>
  );
};

const StarterNotice = styled.div`
  display: flex;
  gap: 12px;
  align-items: flex-start;
  margin: 0 0 20px;
  padding: 14px 16px;
  border: 1px solid ${ob.warning};
  border-radius: 12px;
  background: ${ob.warningSoft};
  color: ${ob.warning};
  font-size: 14px;
  line-height: 1.5;
  svg {
    flex-shrink: 0;
    margin-top: 2px;
  }
  strong {
    display: block;
    margin-bottom: 2px;
  }
  a {
    color: inherit;
    font-weight: 600;
    text-decoration: underline;
  }
`;

const DoneWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 32px 0 24px;
  .badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 120px;
    height: 120px;
    border-radius: 32px;
    background: ${ob.successSoft};
    color: ${ob.success};
  }
  h1 {
    margin: 36px 0 12px;
    font-family: ${ob.fontDisplay};
    font-size: clamp(34px, 6vw, 52px);
    font-weight: 600;
    letter-spacing: -0.02em;
  }
  p {
    max-width: 40rem;
    margin: 0 0 40px;
    font-size: 19px;
    line-height: 2;
    color: ${ob.muted};
  }
`;

const DoneCard = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
  width: 100%;
  max-width: 52rem;
  padding: 24px;
  border: 1px solid ${ob.border};
  border-radius: 28px;
  background: #fff;
  box-shadow: ${ob.shadowCard};
  text-align: left;
  img,
  .ph {
    flex-shrink: 0;
    width: 144px;
    height: 144px;
    border-radius: 24px;
    object-fit: cover;
    background: ${ob.mutedBg};
  }
  .pill {
    display: inline-block;
    align-self: flex-start;
    padding: 6px 14px;
    border-radius: 999px;
    background: ${ob.warningSoft};
    color: ${ob.warning};
    font-size: 14px;
    font-weight: 700;
    letter-spacing: 0.02em;
    text-transform: uppercase;
  }
  div {
    display: flex;
    flex-direction: column;
    gap: 8px;
    min-width: 0;
  }
  b {
    font-family: ${ob.fontDisplay};
    font-size: 26px;
    font-weight: 600;
    overflow-wrap: anywhere;
  }
  .loc {
    font-size: 17px;
    color: ${ob.muted};
  }
  @media (max-width: 560px) {
    flex-direction: column;
    align-items: flex-start;
    img,
    .ph {
      width: 100%;
      height: auto;
      aspect-ratio: 4 / 3;
    }
  }
`;

const DoneActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 16px;
  margin-top: 32px;
`;

const Confirmation: React.FC = () => {
  let navigate = useNavigate();

  const {
    place,
    type,
    subType,
    location,
    detail,
    pricingDetails,
    bookingSettings,
    propertyId,
    additionalChargesMap,
    onboardingStage,
    amenitiesObject,
    offeringsMap,
  } = useSelector((select: any) => select) as ReduxState;

  const [cancellationCatalog, setCancellationCatalog] = useState<
    CancellationPolicyTemplate[]
  >([]);

  useEffect(() => {
    getCancellationCatalog()
      .then(setCancellationCatalog)
      .catch(() => setCancellationCatalog([]));
  }, []);

  // Compute effective basePrice for display: prefer persisted values over server defaults
  const effectiveBasePrice = useMemo(() => {
    try {
      const currentPropertyId = propertyId || localStorage.getItem('id');
      const savedBasePrices = localStorage.getItem(
        `userBasePrices_${currentPropertyId}`,
      );
      if (savedBasePrices) {
        const parsed = JSON.parse(savedBasePrices);
        const hasUserValues = Object.values(parsed).some(
          (v: any) => v && typeof v.start === 'number',
        );
        if (hasUserValues) return parsed;
      }
    } catch (_) {}
    return pricingDetails?.basePrice;
  }, [propertyId, pricingDetails?.basePrice]);

  const paymentModeSummary = useMemo(
    () => formatBookingPaymentModeSummary(bookingSettings),
    [
      bookingSettings?.partialPaymentAllowed,
      bookingSettings?.partialPaymentPercentage,
    ],
  );

  // Starter: the title / description guests will see (brand name removed), from the backend.
  const [starterPreview, setStarterPreview] = useState<{ title: string; description: string } | null>(null);

  const descriptionConfirmationHtml = useMemo(() => {
    const raw = starterPreview?.description?.trim()
      ? starterPreview.description
      : detail?.displayDescription?.trim()
      ? detail.displayDescription
      : detail?.description?.trim()
      ? detail.description
      : '';
    if (!raw) return '';
    return DOMPurify.sanitize(raw, { USE_PROFILES: { html: true } });
  }, [detail?.description, detail?.displayDescription, starterPreview?.description]);

  const [onboardingPending, setOnboardingPending] = useState<boolean>(true);
  const [previewBannerVisible, setPreviewBannerVisible] = useState(true);
  const previewBannerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = previewBannerRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => setPreviewBannerVisible(entry.isIntersecting),
      { threshold: 0.1 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [onboardingPending]);
  const [isCheckboxChecked, setIsCheckboxChecked] = useState<boolean>(false);
  const [propertyDetails, setPropertyDetails] = useState<PropertyDetails>({});
  const [propertyPhotos, setPropertyPhotos] = useState<PropertyPhotos>({});
  const [propertyVideo, setPropertyVideo] = useState<string | null>(null);
  const [propertyWalkthroughVideo, setPropertyWalkthroughVideo] = useState<string | null>(null);
  const [, setPropertyBookingDetails] = useState<any>({});
  const [, setPropertyPricingDetails] = useState<any>({});
  const [calendarSummary, setCalendarSummary] = useState<{
    upcomingBookedDays: number;
    blockedDays: number;
    completedBookedDays: number;
  }>({
    upcomingBookedDays: 0,
    blockedDays: 0,
    completedBookedDays: 0,
  });

  const [propertyTypeListMap, setPropertyTypeListMap] =
    useState<PropertyTypeListMap | null>(null);
  const [propertyOfferingsMap, setPropertyOfferingsMap] =
    useState<PropertyOfferingsMap | null>(null);
  /** Guard: run images/videos/pricing/booking/details fetches only once per property */
  const hasLoadedDetailsForPropertyIdRef = useRef<string | null>(null);
  const [statesMap, setStatesMap] = useState<Record<number, string>>({});

  const { BackConfirmModal } = useOnboardingBackConfirm();

  const routerLocation = useLocation();
  const useQuery = (): URLSearchParams => {
    return new URLSearchParams(routerLocation.search);
  };
  let query = useQuery();
  const hostPropertyId = query.get('id');

  /** Starter plan: guests see generic title/description, so tell the host why theirs aren't shown. */
  const [starterCopy, setStarterCopy] = useState(false);
  useEffect(() => {
    const id = hostPropertyId || localStorage.getItem('id');
    if (!id) return;
    let chosenHere = false;
    try {
      chosenHere = sessionStorage.getItem(`starterChosen_${id}`) === '1';
    } catch {
      // ignore
    }
    if (chosenHere) setStarterCopy(true);
    HostPlanPurchaseService.isStarterCopy(id)
      .then((v) => {
        setStarterCopy(v || chosenHere);
        if (!(v || chosenHere)) return null;
        return HostPlanPurchaseService.starterPreview(id);
      })
      .then((preview) => {
        if (preview && (preview.title || preview.description)) setStarterPreview(preview);
      })
      .catch(() => {});
  }, [hostPropertyId]);
  /** Arrived from the "Let us list it for you" import: shows that flow's rail and steps. */
  const isImportFlow = query.get('flow') === 'import';
  // Back on a Review page: any remembered edit return target has been used up.
  useEffect(() => {
    clearImportEdit();
  }, []);
  // Admins skip the plan step entirely (their listings are plan-free), so "back" goes to the step before it.
  const isAdmin = isAdminUser();
  const planPath = (id: unknown): string =>
    isAdmin
      ? isImportFlow
        ? `/host/import-listing?id=${id}&step=missing`
        : `/host/house-rules?id=${id}`
      : `/host/hosting-plan?id=${id}${isImportFlow ? '&flow=import' : ''}`;
  const dispatch = useDispatch();

  const getPropertyTypeListMap = async (): Promise<void> => {
    if (!propertyTypeListMap) {
      const responseData = await PropertyService.getPropertyTypeList();
      const { responseInformation, result } = responseData;
      if ((responseInformation as any)?.responseCode === 0) {
        const dataMap: PropertyTypeListMap = {};
        result.forEach((item) => {
          dataMap[item.id] = { ...item };
        });
        setPropertyTypeListMap({ ...dataMap });
      }
    }
  };

  const transformBookingSetting = (data: any): any =>
    transformBookingSettingsFromApi(data);

  // const getCityMap = async (stateId) => {
  //   if (!cityMap && stateId) {
  //     const responseData = await LocationService.getCityListForState(stateId);
  //     const { responseInformation, result } = responseData;
  //     if (responseInformation.responseCode === 0) {
  //       const dataMap = {};
  //       result.forEach((item) => {
  //         dataMap[item.id] = { ...item };
  //       });
  //       setCityMap(dataMap);
  //       return dataMap;
  //     }
  //   }
  // };

  // const getStatesMap = async () => {
  //   if (!statesMap) {
  //     const responseData = await LocationService.getSupportedStateList();
  //     const { responseInformation, result } = responseData;
  //     if (responseInformation.responseCode === 0) {
  //       const dataMap = {};
  //       result.forEach((item) => {
  //         dataMap[item.id] = { ...item };
  //       });
  //       setStatesMap(dataMap);
  //     }
  //   }
  // };

  const getPropertyOfferingsMap = async (): Promise<void> => {
    if (!propertyOfferingsMap) {
      const responseData = await PropertyService.getPropertyDetailsFieldList();

      const { responseInformation, result } = responseData;
      if ((responseInformation as any)?.responseCode === 0) {
        const offerMap: PropertyOfferingsMap = {};
        result.forEach((element) => {
          offerMap[element.id] = element.name;
        });
        setPropertyOfferingsMap({ ...offerMap });
      }
    }
  };

  const getPropertyDetailsById = useCallback(async () => {
    if (propertyTypeListMap && propertyOfferingsMap) {
      const responseData = await PropertyService.getPropertyDetailsById(
        hostPropertyId || propertyId,
      );
      const { responseInformation, result } = responseData;
      if ((responseInformation as any)?.responseCode === 0) {
        const savedPropertyDetails = result;
        // If you need to transform, do so without city/state mapping
        const dataToSet = transformPropertyDataFromServer(
          savedPropertyDetails as any,
          propertyTypeListMap,
          propertyOfferingsMap,
        );
        setPropertyDetails(dataToSet);
        dispatch(overwritePropertyDetails(dataToSet));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hostPropertyId, propertyTypeListMap, propertyOfferingsMap]);

  const getPropertyPhotos = useCallback(() => {
    PropertyService.getPhotos(hostPropertyId || propertyId)
      .then((responseData) => {
        const { responseInformation } = responseData;
        if ((responseInformation as any)?.responseCode === 0) {
          setPropertyPhotos(responseData.result);
          dispatch(setImages(responseData.result));
          dispatch(addStoredImages(responseData.result?.imageUrlList));
          if (responseData.result?.thumbnail) {
            dispatch(addThumbnailUrl(responseData.result?.thumbnail));
          }
        }
        //Update in store and read here as well
      })
      .catch((error) => {
        // Handle error
        console.error('Error fetching data:', error);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hostPropertyId, onboardingStage]);

  const getPropertyVideo = useCallback(() => {
    PropertyService.getPropertyVideo(hostPropertyId || propertyId)
      .then((responseData) => {
        const { responseInformation } = responseData;
        if ((responseInformation as any)?.responseCode === 0) {
          const urlFromServer = responseData?.result?.videoUrl ?? null;
          setPropertyVideo(urlFromServer);
          dispatch(addVideoUrl(urlFromServer));

          const walkthroughUrlFromServer = responseData?.result?.walkthroughVideoUrl ?? null;
          setPropertyWalkthroughVideo(walkthroughUrlFromServer);
          dispatch(addWalkthroughVideoUrl(walkthroughUrlFromServer));
        }
        //Update in store and read here as well
      })
      .catch((error) => {
        // Handle error
        console.error('Error fetching data:', error);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hostPropertyId, onboardingStage]);

  const getBookingSettingsForProperty = useCallback(() => {
    PropertyService.getBookingSettingsByPropertyId(hostPropertyId || propertyId)
      .then((responseData) => {
        const { responseInformation, result } = responseData;
        if ((responseInformation as any)?.responseCode === 0) {
          const dataSet = transformBookingSetting(result);
          if (dataSet) {
            setPropertyBookingDetails(dataSet);
            dispatch(addBookingSettings(dataSet as any));
          }
        }
        //Update in store and read here as well
      })
      .catch((error) => {
        // Handle error
        console.error('Error fetching data:', error);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hostPropertyId, onboardingStage]);

  const getPricingDetailsForProperty = useCallback(() => {
    PricingService.getPropertyPricingDetails(hostPropertyId || propertyId)
      .then((responseData) => {
        const { responseInformation, result } = responseData;
        if ((responseInformation as any)?.responseCode === 0) {
          const dataSet = transformPropertyPricing(result);

          // Prefer persisted base price percentages from localStorage (set on Pricing page)
          try {
            const currentPropertyId =
              hostPropertyId || propertyId || localStorage.getItem('id');
            const savedBasePrices = localStorage.getItem(
              `userBasePrices_${currentPropertyId}`,
            );
            if (savedBasePrices) {
              const parsedBasePrices = JSON.parse(savedBasePrices);
              const hasUserBasePriceValues = Object.values(
                parsedBasePrices,
              ).some((value: any) => value && typeof value.start === 'number');
              if (hasUserBasePriceValues) {
                dataSet.basePrice = parsedBasePrices;
              }
            }
          } catch (e) {
            // noop: fallback to server values
          }

          setPropertyPricingDetails(dataSet);
          dispatch(overwritePricing(dataSet));
        }
      })
      .catch((error) => {
        console.error('Error fetching data:', error);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hostPropertyId, onboardingStage]);

  const getCalendarSummary = useCallback(async () => {
    try {
      const currentPropertyId =
        hostPropertyId || propertyId || localStorage.getItem('id');
      if (!currentPropertyId) return;

      const response = await CalendarService.getHostCalendar(currentPropertyId);
      if (response.responseInformation?.responseCode !== 0 || !response.result)
        return;

      const result = response.result as
        | HostCalendarDayDTO[]
        | { dates?: HostCalendarDayDTO[]; defaultMinNights?: number };
      const days = Array.isArray(result) ? result : result?.dates || [];

      const now = new Date();
      const currentYear = now.getFullYear();
      const currentMonth = now.getMonth();
      const todayStr = now.toISOString().slice(0, 10);

      let upcomingBookedDays = 0;
      let blockedDays = 0;
      let completedBookedDays = 0;

      days.forEach((day) => {
        const d = new Date(day.date);
        if (d.getFullYear() !== currentYear || d.getMonth() !== currentMonth) return;
        if (day.status === 'BLOCKED') blockedDays += 1;
        if (day.status === 'BOOKED') {
          if (day.date >= todayStr) upcomingBookedDays += 1;
          else completedBookedDays += 1;
        }
      });

      setCalendarSummary({ upcomingBookedDays, blockedDays, completedBookedDays });
    } catch (error) {
      console.error('Error fetching calendar summary:', error);
    }
  }, [hostPropertyId, propertyId]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Re-fetch calendar data on every navigation so blocked/booked stats are always current.
  useEffect(() => {
    const pid = hostPropertyId || propertyId || localStorage.getItem('id');
    if (!pid) return;
    getCalendarSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routerLocation.key]);

  // Init: apply persisted base price immediately on mount to avoid showing 70%
  useEffect(() => {
    try {
      const currentPropertyId =
        hostPropertyId || propertyId || localStorage.getItem('id');
      const savedBasePrices = localStorage.getItem(
        `userBasePrices_${currentPropertyId}`,
      );
      if (!savedBasePrices) return;
      const parsedBasePrices = JSON.parse(savedBasePrices);
      const hasUserBasePriceValues = Object.values(parsedBasePrices).some(
        (value: any) => value && typeof value.start === 'number',
      );
      if (!hasUserBasePriceValues) return;

      if (pricingDetails && pricingDetails.basePrice) {
        const differs =
          JSON.stringify(pricingDetails.basePrice) !==
          JSON.stringify(parsedBasePrices);
        if (differs) {
          dispatch(
            overwritePricing({
              ...pricingDetails,
              basePrice: parsedBasePrices,
            } as any),
          );
        }
      }
    } catch (_) {
      // ignore
    }
  }, [hostPropertyId, propertyId, dispatch]);

  // 1) Load lookup maps once when we have a property id (so getPropertyDetailsById can run later)
  useEffect(() => {
    const getAllData = async () => {
      if (hostPropertyId || propertyId) {
        await getPropertyTypeListMap();
        await getPropertyOfferingsMap();
        LocationService.getSupportedStateList().then((res) => {
          if ((res.responseInformation as any)?.responseCode === 0) {
            const map: Record<number, string> = {};
            res.result.forEach((s: { id: number; name: string }) => { map[s.id] = s.name; });
            setStatesMap(map);
          }
        });
        await getPropertyDetailsById();
        getPropertyPhotos();
        getPropertyVideo();
        getBookingSettingsForProperty();
        getPricingDetailsForProperty();
        getCalendarSummary();
      }
    };
    getAllData();
  }, [hostPropertyId, propertyId]);

  // 2) Load details, videos, booking, pricing exactly once per property (when maps are ready)
  useEffect(() => {
    const currentId = hostPropertyId || propertyId || null;
    if (!currentId || !propertyTypeListMap || !propertyOfferingsMap) return;
    if (hasLoadedDetailsForPropertyIdRef.current === currentId) return;
    hasLoadedDetailsForPropertyIdRef.current = currentId;

    getPropertyDetailsById();
    getPropertyVideo();
    getBookingSettingsForProperty();
    getPricingDetailsForProperty();
    // Intentionally depend only on stable ids and maps so this runs once per property
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    getBookingSettingsForProperty,
    getCalendarSummary,
    getPricingDetailsForProperty,
    getPropertyDetailsById,
    getPropertyPhotos,
    hostPropertyId,
  ]);

  // Secondary guard: ensure persisted base price percentages are always applied
  useEffect(() => {
    try {
      if (!pricingDetails || !pricingDetails.basePrice) return;
      const currentPropertyId =
        hostPropertyId || propertyId || localStorage.getItem('id');
      const savedBasePrices = localStorage.getItem(
        `userBasePrices_${currentPropertyId}`,
      );
      if (!savedBasePrices) return;
      const parsedBasePrices = JSON.parse(savedBasePrices);
      const hasUserBasePriceValues = Object.values(parsedBasePrices).some(
        (value: any) => value && typeof value.start === 'number',
      );
      if (!hasUserBasePriceValues) return;

      // If server/base state differs from persisted values, override for display via store
      const currentBase = pricingDetails.basePrice;
      const differs =
        JSON.stringify(currentBase) !== JSON.stringify(parsedBasePrices);
      if (differs) {
        dispatch(
          overwritePricing({
            ...pricingDetails,
            basePrice: parsedBasePrices,
          } as any),
        );
      }
    } catch (_) {
      // ignore
    }
  }, [pricingDetails, hostPropertyId, propertyId, dispatch]);

  const onConfirmClick = async (): Promise<void> => {
    try {
      const effectivePropertyId =
        hostPropertyId || propertyId || localStorage.getItem('id');
      if (!effectivePropertyId) {
        notification.error({
          message: 'Missing property id',
          description: 'Could not determine property id to confirm.',
        });
        return;
      }

      // An admin who listed this property as a Unique Experience chose its tags before it had an id: save them now.
      // A failure here is reported but never blocks the listing; the tags can be added later in the admin page.
      if (isAdminSession()) {
        const draft = readDraft();
        if (draft) {
          try {
            await UniqueStayService.setTags(effectivePropertyId, draft);
            clearDraft();
          } catch (err: any) {
            notification.warning({
              message: 'Unique experience tags were not saved',
              description:
                err?.response?.data?.responseInformation?.message ??
                'You can add them later under Unique Stay Tags in the admin menu.',
            });
          }
        }
      }

      const propertyIdNum = Number(effectivePropertyId);
      const propertyIdPayload =
        Number.isFinite(propertyIdNum) && propertyIdNum > 0
          ? propertyIdNum
          : effectivePropertyId;

      // For already-active old listings (onboardingStage >= 5 and we are in edit
      // mode), completePropertyOnboarding returns 400 because they are already
      // onboarded. Treat the edit-save as immediately confirmed instead.
      const isAlreadyOnboarded =
        !!hostPropertyId && typeof onboardingStage === 'number' && onboardingStage >= 5;

      if (isAlreadyOnboarded) {
        setOnboardingPending(false);
        notification.success({
          message: 'Listing updated',
          description: 'Your listing has been updated successfully.',
        });
        return;
      }

      // New listing flow: try without onboardingStage first, then with it.
      let res = await PropertyService.completePropertyOnboarding({
        propertyId: propertyIdPayload,
      });
      if (res?.responseInformation?.responseCode !== 0) {
        res = await PropertyService.completePropertyOnboarding({
          propertyId: propertyIdPayload,
          onboardingStage: 5,
        });
      }

      // If the API still returns 400 / non-zero it may mean the listing was
      // already onboarded via a previous session — treat that as success too.
      const httpStatus = (res as any)?._httpStatus;
      if (
        res?.responseInformation?.responseCode === 0 ||
        httpStatus === 400
      ) {
        setOnboardingPending(false);
        dispatch(updateStore(true));
        notification.success({
          message: 'Listing submitted',
          description:
            'Your listing has been submitted and is pending admin approval.',
        });
      } else {
        notification.error({
          message: 'Confirm failed',
          description: getApiResponseMessage(
            res,
            'Failed to confirm listing. Please try again.',
          ),
        });
      }
    } catch (error) {
      console.log('confirm error', error);
      notification.error({
        message: 'Confirm failed',
        description: getUserFriendlyError(
          error,
          'An error occurred while confirming listing.',
        ),
      });
      return;
    }
  };

  const offeringsMapToShow = !isEmpty(propertyDetails?.offeringsMap)
    ? propertyDetails?.offeringsMap
    : offeringsMap;
  const amenitiesObjectToShow = !isEmpty(propertyDetails?.amenitiesObject)
    ? propertyDetails?.amenitiesObject
    : amenitiesObject;

  const pid = hostPropertyId || propertyId;

  const previewEid = hostPropertyId || propertyId || localStorage.getItem('id');
  const goEdit = (path: string, extra = ''): void => {
    // The edit pages bring the host back to this exact Review page, in the same flow.
    startImportEdit(pid, `/host/acknowledgement?id=${pid}${isImportFlow ? '&flow=import' : ''}`);
    const flow = isImportFlow && path === '/host/hosting-plan' ? '&flow=import' : '';
    navigate(`${path}?id=${pid}${extra}${flow}`);
  };

  const allowLiveWalkthrough = useSelector((s: any) => s.allowLiveWalkthrough) as boolean | undefined;

  /* ---- values for the review layout ---- */
  const photoUrls = (() => {
    const list = propertyPhotos?.imageUrlList || {};
    const seen = new Set<string>();
    const urls: string[] = [];
    Object.keys(list).forEach((category) => {
      (list[category] || []).forEach((url) => {
        const norm = normalizeImageUrlForCompare(url);
        if (!norm || seen.has(norm)) return;
        seen.add(norm);
        urls.push(url);
      });
    });
    const cover = propertyPhotos?.thumbnail ? Object.values(propertyPhotos.thumbnail)[0] : null;
    if (cover) {
      const index = urls.findIndex((u) => normalizeImageUrlForCompare(u) === normalizeImageUrlForCompare(cover));
      if (index > 0) urls.unshift(...urls.splice(index, 1));
    }
    return urls;
  })();

  const offeringValue = (re: RegExp): number | string | undefined => {
    const map = offeringsMapToShow || {};
    const key = Object.keys(map).find((k) => re.test(String(map[k]?.detailsName || '')));
    return key ? map[key]?.detailsValue : undefined;
  };
  const stats: { label: string; icon: React.ReactNode }[] = [];
  const guestsValue = offeringValue(/guest/i);
  const bedroomsValue = offeringValue(/bedroom/i);
  const bedsValue = offeringValue(/^beds?$/i);
  const bathsValue = offeringValue(/bath/i);
  if (guestsValue != null)
    stats.push({ label: `${guestsValue} guests`, icon: <LuUsers size={18} /> });
  if (bedroomsValue != null || bedsValue != null)
    stats.push({
      label: [
        bedroomsValue != null ? `${bedroomsValue} bedrooms` : '',
        bedsValue != null ? `${bedsValue} beds` : '',
      ]
        .filter(Boolean)
        .join(' · '),
      icon: <LuBedDouble size={18} />,
    });
  if (bathsValue != null) stats.push({ label: `${bathsValue} baths`, icon: <LuBath size={18} /> });

  const stateLabel = /^\d+$/.test(String(location?.state))
    ? statesMap[Number(location?.state)] || location?.state
    : location?.state;
  const locationText = [location?.district, location?.city, stateLabel]
    .filter(Boolean)
    .join(', ');
  // "Other" shows what the host typed instead of the word "Other".
  const placeLabel =
    /^other$/i.test(place?.name ?? '') && place?.other ? place.other : place?.name;
  const typeText = [placeLabel, type?.name].filter(Boolean).join(' · ');

  /* Starter: guests see a brand-free title, so show the same one here (mirrors the backend's
     StarterListingService: "<n>-BHK <property type> in <locality or city>"). */
  const starterTitle = (() => {
    const bedrooms = Number(bedroomsValue);
    const where = (location as any)?.locality || location?.city;
    const parts = [
      bedrooms > 0 ? `${bedrooms}-BHK ` : '',
      placeLabel || 'Stay',
      where ? ` in ${where}` : '',
    ];
    return parts.join('').slice(0, 100);
  })();
  /** The server sent a brand-free copy (e.g. a listing an admin imported that is not Host Pro). */
  const serverBrandFree = !!(detail?.displayTitle || detail?.displayDescription);
  const displayTitle = starterCopy
    ? starterPreview?.title || detail?.displayTitle || starterTitle
    : detail?.displayTitle || detail?.title;

  const selectedPolicy = bookingSettings?.cancellationPolicyCode
    ? cancellationCatalog.find((c) => c.code === bookingSettings.cancellationPolicyCode)
    : undefined;
  const customPolicy = (bookingSettings as any)?.customCancellationPolicy;
  const cancellationLine =
    bookingSettings?.cancellationPolicyCode === CUSTOM_POLICY_CODE && customPolicy
      ? `Custom — ${describePolicy(customPolicy).join(' ')}`
      : selectedPolicy
      ? [selectedPolicy.displayName, selectedPolicy.description].filter(Boolean).join(' — ')
      : '';
  const stayLine = [
    bookingSettings?.checkInTime ? `Check-in from ${bookingSettings.checkInTime}` : '',
    bookingSettings?.checkOutTime ? `check-out by ${bookingSettings.checkOutTime}` : '',
    ...(bookingSettings?.restrictions || []),
  ]
    .filter(Boolean)
    .join(' · ');

  const money = (value: unknown): string =>
    value != null && Number(value) > 0 ? `₹${Number(value).toLocaleString('en-IN')}` : '';
  const priceText = money(pricingDetails?.defaultPrice) || '—';
  const weekendPriceText = money((pricingDetails as any)?.weekendDefaultPrice);
  const minNightsText = (pricingDetails as any)?.weekdayMinNights ?? 1;
  const hostingPlanLabel = (() => {
    try {
      const raw = localStorage.getItem('userDetails');
      return raw && JSON.parse(raw)?.planType === 'premium' ? 'Host Pro' : 'Starter';
    } catch (_) {
      return 'Starter';
    }
  })();

  return (
    <>
      {onboardingPending ? (
        <OnboardingShell
          section={isImportFlow ? 3 : 9}
          railItems={isImportFlow ? IMPORT_SECTIONS : undefined}
          maxWidth="58rem"
          hideActions
          headerBack={() => navigate(planPath(pid))}
          modal={BackConfirmModal}
        >
          <TitleRow>
            <h1>Review your listing</h1>
            {previewEid ? (
              <div ref={previewBannerRef}>
                <PreviewPill
                  type="button"
                  onClick={() => openPreviewInNewTab(previewEid, displayTitle)}
                >
                  <FiEye size={18} /> Preview as guest <FiArrowRight size={16} />
                </PreviewPill>
              </div>
            ) : null}
          </TitleRow>

          {starterCopy ? (
            <StarterNotice role="status">
              <FiInfo size={18} />
              <div>
                <strong>Some branding details have been removed</strong>
                You chose the Starter plan, so guests see your title and description with the brand name
                removed. Your original text is saved and comes back when you upgrade to Host Pro.{' '}
                <a href={planPath(pid)}>View plans</a>
              </div>
            </StarterNotice>
          ) : serverBrandFree ? (
            <StarterNotice role="note">
              <FiInfo size={18} />
              <div>
                <strong>Some branding details have been removed</strong>
                Because this listing is not on Host Pro, we have changed the title and description a little: the
                brand name is removed. This is how guests will see them.
              </div>
            </StarterNotice>
          ) : null}

          <ReviewCard>
            <Mosaic>
              {photoUrls.length === 0 ? (
                <div className="tile cover empty">🏡</div>
              ) : (
                <>
                  <div className="tile cover">
                    <img src={photoUrls[0]} alt={displayTitle || 'Property'} />
                  </div>
                  {photoUrls.slice(1, 3).map((url, i, arr) => (
                    <div className="tile" key={url}>
                      <img src={url} alt="property" />
                      {i === 0 ? (
                        <span className="edit">
                          <EditPill type="button" onClick={() => goEdit('/host/photo-upload')}>
                            <FiEdit2 size={14} /> Edit
                          </EditPill>
                        </span>
                      ) : null}
                      {i === arr.length - 1 && photoUrls.length > 3 ? (
                        <span className="more">+{photoUrls.length - 3} more</span>
                      ) : null}
                    </div>
                  ))}
                  {photoUrls.length === 1 ? (
                    <div className="tile empty">
                      <span className="edit">
                        <EditPill type="button" onClick={() => goEdit('/host/photo-upload')}>
                          <FiEdit2 size={14} /> Edit
                        </EditPill>
                      </span>
                    </div>
                  ) : null}
                </>
              )}
            </Mosaic>

            <Body>
              <Main>
                <HeadRow>
                  <div>
                    <h2 className="name">{displayTitle || 'Your listing'}</h2>
                    {locationText ? (
                      <div className="where">
                        <FiMapPin size={18} /> {locationText}
                      </div>
                    ) : null}
                    {typeText ? <div className="kind">{typeText}</div> : null}
                  </div>
                  <div className="edits">
                    <EditPill type="button" onClick={() => goEdit('/host/title-and-description')}>
                      <FiEdit2 size={14} /> Edit
                    </EditPill>
                    <EditPill type="button" onClick={() => goEdit('/host/location')}>
                      <FiEdit2 size={14} /> Edit
                    </EditPill>
                  </div>
                </HeadRow>

                <Stats>
                  {stats.map((item) => (
                    <span className="stat" key={item.label}>
                      {item.icon} {item.label}
                    </span>
                  ))}
                  <span className="edit">
                    <EditPill
                      type="button"
                      aria-label="Edit property type and capacity"
                      onClick={() => goEdit('/host/description')}
                    >
                      <FiEdit2 size={14} /> Edit
                    </EditPill>
                  </span>
                </Stats>

                <BlockRow>
                  <div className="grow">
                    {descriptionConfirmationHtml ? (
                      <DescriptionHtml
                        // eslint-disable-next-line react/no-danger -- sanitized with DOMPurify
                        dangerouslySetInnerHTML={{ __html: descriptionConfirmationHtml }}
                        style={{ color: '#6b6b6b', fontSize: 16, lineHeight: 1.7 }}
                      />
                    ) : (
                      <p className="text">No description yet.</p>
                    )}
                  </div>
                  <EditPill type="button" onClick={() => goEdit('/host/title-and-description')}>
                    <FiEdit2 size={14} /> Edit
                  </EditPill>
                </BlockRow>

                <BlockRow>
                  <div className="grow">
                    <h2>What this place offers</h2>
                    <Offers>
                      {(amenitiesObjectToShow || []).map((item, index) => (
                        <span className="chip" key={index}>
                          {item.name}
                        </span>
                      ))}
                      {!(amenitiesObjectToShow || []).length ? (
                        <span className="chip">No amenities added</span>
                      ) : null}
                    </Offers>
                    {allowLiveWalkthrough ? (
                      <LiveNote>
                        <LuVideo size={18} /> Live walkthrough on request
                      </LiveNote>
                    ) : null}
                  </div>
                  <EditPill type="button" onClick={() => goEdit('/host/amenities')}>
                    <FiEdit2 size={14} /> Edit
                  </EditPill>
                </BlockRow>

                <BlockRow>
                  <div className="grow">
                    <h2>Stay details, rules &amp; cancellation</h2>
                    <Lines style={{ marginTop: 16 }}>
                      <p>{stayLine}</p>
                      {cancellationLine ? <p>Cancellation — {cancellationLine}</p> : null}
                    </Lines>
                  </div>
                  <div className="edits" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    <EditPill type="button" onClick={() => goEdit('/host/booking-settings')}>
                      <FiEdit2 size={14} /> Edit
                    </EditPill>
                    <EditPill type="button" onClick={() => goEdit('/host/house-rules', '&edit=1')}>
                      <FiEdit2 size={14} /> Edit
                    </EditPill>
                  </div>
                </BlockRow>
              </Main>

              <Aside>
                <div className="top">
                  <div>
                    <div className="price">
                      {priceText} <small>/ night</small>
                    </div>
                  </div>
                  <EditPill type="button" onClick={() => goEdit('/host/property-listing')}>
                    <FiEdit2 size={14} /> Edit
                  </EditPill>
                </div>
                {weekendPriceText ? (
                  <p className="weekend">{weekendPriceText} / night on weekends</p>
                ) : null}

                <div className="box">
                  <div className="head">
                    <h3>Your dates</h3>
                    <EditPill type="button" onClick={() => goEdit('/host/property-listing')}>
                      <FiEdit2 size={14} /> Edit
                    </EditPill>
                  </div>
                  <p>
                    Dates to be confirmed
                    <br />
                    Minimum {minNightsText} nights
                  </p>
                </div>

                <p className="pay">{paymentModeSummary}</p>

                {!isAdmin && (
                  <div className="box">
                    <div className="head">
                      <h3>Hosting plan</h3>
                      <EditPill type="button" onClick={() => goEdit('/host/hosting-plan')}>
                        <FiEdit2 size={14} /> Edit
                      </EditPill>
                    </div>
                    <p>{hostingPlanLabel}</p>
                  </div>
                )}

                <button type="button" className="reserve" disabled>
                  Reserve
                </button>
                <p className="only">Preview only</p>
              </Aside>
            </Body>
          </ReviewCard>

          {/* ── Confirm ── */}
          <ConfirmSection>
            <div className="row">
              <input
                type="checkbox"
                id="confirmCheckbox"
                checked={isCheckboxChecked}
                onChange={() => setIsCheckboxChecked(!isCheckboxChecked)}
              />
              <label htmlFor="confirmCheckbox">
                I confirm that all the above details are accurate and I agree to Unpaqd's{' '}
                <a target="_blank" href="/term-of-use" rel="noreferrer">Terms of Service</a>
                {' '}and{' '}
                <a target="_blank" href="/privacy-policy" rel="noreferrer">Privacy Policy</a>.
              </label>
            </div>
          </ConfirmSection>

          <BottomRow style={{ alignItems: 'flex-start' }}>
            <GhostButton type="button" onClick={() => navigate(planPath(pid))}>
              <FiArrowLeft size={16} /> Back
            </GhostButton>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 10 }}>
              <PrimaryButton
                type="button"
                onClick={onConfirmClick}
                disabled={!isCheckboxChecked}
              >
                Confirm &amp; Submit Listing <FiArrowRight size={16} />
              </PrimaryButton>
              {!isCheckboxChecked ? (
                <div
                  role="note"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '8px 12px',
                    border: '1px solid rgba(255, 113, 15, 0.25)',
                    borderRadius: 12,
                    background: 'rgba(255, 240, 230, 0.6)',
                    fontSize: 13,
                    color: '#1a1a1a',
                  }}
                >
                  <LuSparkles size={14} color="#ff710f" />
                  <span>Tick the checkbox above to confirm your details and submit.</span>
                </div>
              ) : null}
            </div>
          </BottomRow>
        </OnboardingShell>
      ) : (
        <OnboardingShell
          section={isImportFlow ? 4 : 9}
          railItems={isImportFlow ? IMPORT_SECTIONS : undefined}
          hideActions
          modal={BackConfirmModal}
        >
          <SuccessView
            detail={detail}
            location={location}
            propertyPhotos={propertyPhotos}
            onEdit={() => setOnboardingPending(true)}
            onDashboard={() => navigate(HOME_PAGE)}
            onAddAnother={() => {
              localStorage.removeItem('newPropertId');
              dispatch(updateStore(true));
              // Let the host choose how to list (List It Yourself / Let Us List It For You).
              navigate(HOME_PAGE, { state: { showListingOptions: true } });
            }}
          />
        </OnboardingShell>
      )}

      {/* Floating pill — visible on review form when banner has scrolled out of view */}
      {onboardingPending && !previewBannerVisible && previewEid ? (
        <FloatingPreview
          type="button"
          onClick={() => openPreviewInNewTab(previewEid, displayTitle)}
        >
          <span>👁</span> Preview as Guest
        </FloatingPreview>
      ) : null}
    </>
  );
};

export default Confirmation;